import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Spin } from "antd";
import { MONTHS_LISTS_PASCAL } from "src/entity/common/time.constant";
import { formatNumber } from "src/utils/process-number";
import { EnumReportScope } from "src/entity/main-report/main-report.entity";
import { sortYears } from "src/utils/array";

const measure = 5000000; // for decide using any plot option !
interface SlotSeries {
  name: string;
  data: number[];
}

interface StackedColumnChartProps {
  loading: boolean;
  data: SlotSeries[];
  scope: EnumReportScope;
  selectedYear: string[];
}

const StackedColumnChart: React.FC<StackedColumnChartProps> = ({ loading, data, scope, selectedYear }) => {
  const months = scope === EnumReportScope.MONTHLY ? MONTHS_LISTS_PASCAL : sortYears(selectedYear, "asc");

  const getMultipleValue = (diff: number) => {
    const base = 100;
    if (diff <= 10000) return 100000000000000 * base;
    else if (diff >= 10000 && diff < 100000) return 1000000000000000 * base;
    else if (diff >= 100000 && diff < 1000000000000) return 10000 * base;
    else if (diff >= 1000000000000 && diff <= 2200000000000) return base;
    else if (diff > 2200000000000) return base * 1000000000000;
    else return 1000000;
  };

  const maxValue = useMemo(() => {
    return Math.max(...data.flatMap((d) => d.data));
  }, [data]);

  const minValue = useMemo(() => {
    return Math.min(...data.flatMap((d) => d.data));
  }, [data]);

  const everyValueIsZero = useMemo(() => {
    let someGroupNotZero = true;
    for (const item of data) {
      const isAllZero = item.data.every((num) => num == 0);
      if (!isAllZero) someGroupNotZero = false;
    }
    return someGroupNotZero;
  }, [data]);

  const difference = maxValue - minValue;
  // Estimate -> difference = minValue * 120 -> OPEN Some Options !!!!

  // const isLongDifference = maxValue > measure && difference > measure / 5;

  const additionalMultiply = getMultipleValue(difference);
  // console.log("Max & Min : ", { maxValue, minValue, difference });
  // const yaxisPlotOption = isLongDifference
  //   ? {
  //       show: false,
  //       logarithmic: true,
  //       min: 0.01, // Adjust the minimum value to ensure small values are visible
  //       max: maxValue * 1000000000000, // Adjust the maximum value to fit the largest value
  //     }
  //   : { show: false, logarithmic: false, min: 0.1 };
  const yaxisPlotOption = everyValueIsZero
    ? { show: false, logarithmic: false }
    : { show: false, logarithmic: true, min: 0.1, max: maxValue * 10000000 * additionalMultiply };

  // Chart options with type explicitly defined as ApexOptions
  const chartOptions: ApexOptions = {
    chart: {
      type: "bar", // Ensure this matches the expected literal type
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    colors: ["#e1966a", "#f8e384", "#B5E2FA", "#48D1B8", "#FFE9BE", "#FA898B", "#D4A5F9"],
    dataLabels: {
      enabled: false, // Disable labels on the chart stacks
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
            offsetX: -10,
            offsetY: 0,
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 12, // Add rounded borders to the top of each stack
        borderRadiusApplication: "end", // Ensure only the top stack has rounded corners
        columnWidth: "30px", // Set fixed width if there's only one data point
      },
    },
    xaxis: {
      categories: months,
    },
    yaxis: {
      ...yaxisPlotOption,
    },
    legend: {
      position: "top",
      offsetY: 0,
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: (value) => {
          if (value < 1 && value > 0) return `${formatNumber(value, 2)}`;
          return `${formatNumber(value, 2)}`;
        },
      },
    },
  };

  // console.log("[Stacked Column Chart] data : ", data);

  return (
    <div className="w-full">
      {loading ? (
        <Spin spinning />
      ) : (
        <Chart
          options={chartOptions}
          series={data}
          type="bar" // Ensure this matches the expected literal type
          height={480}
        />
      )}
    </div>
  );
};

export default StackedColumnChart;
